import { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import CoinIcon from '../components/CoinIcon';
import TradeDetailPanel from '../components/TradeDetailPanel';
import { HL_WALLET, HL_URL, HL_APP_URL, HL_NETWORK, RAILWAY_URL, hlPost } from '../hyperliquid';
import { tradeResult } from '../tradePnl';
import './Dashboard.css';

function toDateInput(d) { return d.toISOString().slice(0, 10); }

// ── Hyperliquid hook ──────────────────────────────────────────────────────────
function useHyperliquid() {
  const [data, setData] = useState({
    positions: [], orders: [], mids: {}, fundingRates: {},
    accountValue: 0, withdrawable: 0, dailyPnl: 0, loading: true,
  });

  useEffect(() => {
    async function fetchAll() {
      try {
        const [clearinghouse, openOrders, allMids, fills, metaAndCtxs, spotState] = await Promise.all([
  	hlPost({ type: 'clearinghouseState', user: HL_WALLET }),
	hlPost({ type: 'frontendOpenOrders', user: HL_WALLET }),
        hlPost({ type: 'allMids' }),
        hlPost({ type: 'userFills', user: HL_WALLET }),
        hlPost({ type: 'metaAndAssetCtxs' }),
        hlPost({ type: 'spotClearinghouseState', user: HL_WALLET }),
]);

        const todayMs = new Date().setHours(0, 0, 0, 0);
        const dailyPnl = (Array.isArray(fills) ? fills : [])
          .filter(f => f.time >= todayMs)
          .reduce((sum, f) => sum + (parseFloat(f.closedPnl) || 0) - (parseFloat(f.fee) || 0), 0);

        const fundingRates = {};
        if (Array.isArray(metaAndCtxs) && metaAndCtxs.length === 2) {
          const [meta, assetCtxs] = metaAndCtxs;
          (meta.universe || []).forEach((coin, i) => {
            fundingRates[coin.name] = parseFloat(assetCtxs[i]?.funding || 0);
          });
        }
	const usdcBalance = (spotState?.balances || []).find(b => b.coin === 'USDC');
	const totalUsdc = parseFloat(usdcBalance?.total || 0);
	const heldUsdc  = parseFloat(usdcBalance?.hold  || 0);
        setData({
          positions: clearinghouse.assetPositions || [],
          orders: Array.isArray(openOrders) ? openOrders : [],
          mids: allMids || {},
          fundingRates,
          accountValue: totalUsdc,
  	  withdrawable: totalUsdc - heldUsdc,
          dailyPnl,
          loading: false,
        });
      } catch (e) {
        console.error('HL fetch error:', e);
        setData(d => ({ ...d, loading: false }));
      }
    }

    fetchAll();
    const id = setInterval(fetchAll, 5000);
    return () => clearInterval(id);
  }, []);

  return data;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function findSlTp(orders, coin) {
  const coinOrders = orders.filter(o => o.coin === coin);
  const slOrder = coinOrders.find(o =>
    o.orderType?.includes('Stop') || o.triggerCondition?.toLowerCase().startsWith('sl')
  );
  const tpOrder = coinOrders.find(o =>
    o.orderType?.includes('Take Profit') || o.triggerCondition?.toLowerCase().startsWith('tp')
  );
  return { slOrder, tpOrder };
}

function getMatchingSignal(signals, coin, isLong) {
  const direction = isLong ? 'LONG' : 'SHORT';
  return signals.find(s => {
    const sigCoin = (s.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
    return sigCoin === coin && (s.direction || '').toUpperCase() === direction
      && s.action !== 'CLOSE' && s.status === 'executed';
  }) || null;
}

function formatPrice(price) {
  if (price == null || price === 0) return '—';
  if (price >= 10000) return `$${Number(price).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (price >= 1000)  return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1)     return `$${Number(price).toFixed(4)}`;
  return `$${Number(price).toPrecision(4)}`;
}

function formatPnl(val) {
  const n = Number(val);
  if (n === 0) return '$0.00';
  return `${n > 0 ? '+' : '-'}$${Math.abs(n).toFixed(2)}`;
}

function formatDateTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function tsMs(s) {
  return s.timestamp?.toDate ? s.timestamp.toDate().getTime()
       : s.timestamp ? new Date(s.timestamp).getTime() : 0;
}

// ── Trailing Events sub-component (inside open position card) ────────────────
function PositionTrailEvents({ coin, direction, signalMs }) {
  const [expanded, setExpanded] = useState(false);
  const [events,   setEvents]   = useState([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFetching(true);
      try {
        const snap = await getDocs(query(collection(db, 'trail_events'), orderBy('timestamp', 'asc')));
        const filtered = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => {
            if ((e.symbol    || '') !== coin)      return false;
            if ((e.direction || '') !== direction) return false;
            if (signalMs) {
              const et = e.timestamp?.toDate ? e.timestamp.toDate().getTime() : new Date(e.timestamp).getTime();
              if (et < signalMs) return false;
            }
            return true;
          });
        if (!cancelled) { setEvents(filtered); setFetching(false); }
      } catch (err) {
        console.error('Trail events fetch:', err);
        if (!cancelled) setFetching(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [coin, direction, signalMs]);

  function toggle() {
    setExpanded(p => !p);
  }

  function fmtTs(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="pos-trail-wrap">
      <button className="pos-trail-toggle" onClick={toggle}>
        Trailing Events {expanded ? '▲' : '▼'}
        {!fetching && events.length > 0 && <span className="pos-trail-count">{events.length}</span>}
      </button>

      {expanded && (
        <div className="pos-trail-body">
          {fetching && <span className="pos-trail-status">Loading…</span>}
          {!fetching && events.length === 0 && (
            <span className="pos-trail-status">No trailing events for this position yet.</span>
          )}
          {!fetching && events.length > 0 && (
            <div className="pos-trail-list">
              <div className="pos-trail-header">
                <span>Time</span>
                <span>Phase</span>
                <span>Note</span>
                <span>SL change</span>
                <span>Price</span>
              </div>
              {events.map((e, i) => (
                <div key={e.id || i} className="pos-trail-row">
                  <span className="pos-trail-time">{fmtTs(e.timestamp)}</span>
                  <span className={`tdp-trail-phase tdp-trail-phase--${e.phase}`}>Phase {e.phase}</span>
                  <span className="pos-trail-note">{e.note || '—'}</span>
                  <span className="pos-trail-sl">
                    <span style={{ color: 'var(--red)' }}>${(e.old_sl || 0).toFixed(2)}</span>
                    <span className="pos-trail-arrow"> → </span>
                    <span style={{ color: 'var(--green)' }}>${(e.new_sl || 0).toFixed(2)}</span>
                  </span>
                  <span className="pos-trail-price">
                    {e.price ? `$${Number(e.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── PositionRow ───────────────────────────────────────────────────────────────
function PositionRow({ position, orders, mids, fundingRates, signal }) {
  const p           = position.position;
  const coin        = p.coin;
  const szi         = parseFloat(p.szi);
  const isLong      = szi > 0;
  const entryPx     = parseFloat(p.entryPx);
  const markPx      = parseFloat(mids[coin] || 0);
  const unrealPnl   = parseFloat(p.unrealizedPnl || 0);
  const posValue    = Math.abs(parseFloat(p.positionValue || 0));
  const marginUsed  = parseFloat(p.marginUsed || 0);
  const leverageType = p.leverage?.type === 'isolated' ? 'ISO' : 'Cross';
  const leverage    = p.leverage?.value;
  const pnlColor    = unrealPnl >= 0 ? 'var(--green)' : 'var(--red)';
  const roe = marginUsed > 0 ? (unrealPnl / marginUsed) * 100 : 0;
  const tradeId     = signal?.tradeId || null;

  const { slOrder, tpOrder } = findSlTp(orders, coin);
  const slPrice = signal?.sl_price
    ? formatPrice(signal.sl_price)
    : slOrder ? formatPrice(parseFloat(slOrder.triggerPx)) : '—';
  const tpPrice = signal?.tp_price
    ? formatPrice(signal.tp_price)
    : tpOrder ? formatPrice(parseFloat(tpOrder.triggerPx)) : '—';

  const funding         = fundingRates[coin] ?? null;
  const fundingPctStr   = funding !== null ? `${funding >= 0 ? '+' : ''}${(funding * 100).toFixed(4)}%` : null;
  const fundingColorClass = funding === null ? '' : funding > 0 ? 'funding-positive' : funding < 0 ? 'funding-negative' : 'funding-neutral';

  const liqPx      = parseFloat(p.liquidationPx || 0);
  const liqDistPct = liqPx > 0 && markPx > 0 ? Math.abs(markPx - liqPx) / markPx * 100 : null;
  const nearLiq    = liqDistPct !== null && liqDistPct < 5;
  const critLiq    = liqDistPct !== null && liqDistPct < 2;
  const liqBadgeClass = critLiq ? 'liq-dist-badge--crit' : nearLiq ? 'liq-dist-badge--warn' : 'liq-dist-badge--safe';

  const warnSent = useRef(false);
  useEffect(() => {
    if (critLiq && !warnSent.current && markPx > 0 && liqPx > 0) {
      warnSent.current = true;
      fetch(`${RAILWAY_URL}/liq-warning`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: coin, direction: isLong ? 'LONG' : 'SHORT', markPx, liqPx, distance: liqDistPct }),
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [critLiq]);

  const rowClass = [
    'position-row position-row--live',
    critLiq ? 'position-row--crit-liq' : nearLiq ? 'position-row--near-liq' : '',
  ].filter(Boolean).join(' ');

  const strategyName = signal?.strategy || signal?.source || null;
  const signalMs = signal?.timestamp?.toDate
    ? signal.timestamp.toDate().getTime()
    : signal?.timestamp ? new Date(signal.timestamp).getTime() : null;

  return (
    <div className={rowClass}>

      {/* ── Row 1: all numbers ───────────────────────────────────────────── */}
      <div className="pos-row-line1">
        {tradeId && <span className="pos-trade-id-badge">{tradeId}</span>}

        <div className="pos-side-group">
          <span className={`side-badge side-badge--${isLong ? 'long' : 'short'}`}>
            {isLong ? 'LONG' : 'SHORT'}
          </span>
          {leverage && <span className="leverage-badge">{leverage}x</span>}
        </div>

        <div className="pos-coin">
          <CoinIcon base={coin} size={20} />
          <span className="pos-coin-name">{coin}</span>
        </div>

        <div className="pos-data-group">
          <div className="pos-metric-label">Entry</div>
          <div className="pos-metric-value">{formatPrice(entryPx)}</div>
        </div>
        <div className="pos-data-group">
          <div className="pos-metric-label">SL</div>
          <div className="pos-metric-value" style={{ color: 'var(--red)' }}>{slPrice}</div>
        </div>
        <div className="pos-data-group">
          <div className="pos-metric-label">TP</div>
          <div className="pos-metric-value" style={{ color: 'var(--green)' }}>{tpPrice}</div>
        </div>
        <div className="pos-data-group">
          <div className="pos-metric-label">Mark</div>
          <div className="pos-metric-value">{formatPrice(markPx)}</div>
        </div>
        <div className="pos-data-group">
          <div className="pos-metric-label">Size</div>
          <div className="pos-metric-value">${posValue.toFixed(2)}</div>
        </div>
        <div className="pos-data-group">
          <div className="pos-metric-label">Margin</div>
          <div className="pos-metric-value pos-metric-value--lg">
            ${marginUsed.toFixed(2)}<span className="margin-type">{leverageType}</span>
          </div>
        </div>
        <div className="pos-data-group">
          <div className="pos-metric-label">PnL</div>
          <div className="pos-metric-value" style={{ color: pnlColor }}>
            {formatPnl(unrealPnl)} <span style={{ fontSize: '0.82em', opacity: 0.85 }}>({roe >= 0 ? '+' : ''}{roe.toFixed(1)}%)</span>
          </div>
        </div>
        <div className="pos-data-group">
          <div className="pos-metric-label">Funding/8h</div>
          <div className={`pos-metric-value ${fundingColorClass}`}>
            {fundingPctStr ?? '—'}
          </div>
        </div>
      </div>

      {/* ── Row 2: badges only ───────────────────────────────────────────── */}
      <div className="pos-row-line2">
        <span className={`liq-dist-badge ${liqBadgeClass}`}>
          {liqDistPct !== null ? `${liqDistPct.toFixed(1)}% from liq` : 'No liq data'}
        </span>
        {signal?.timestamp && (
          <span className="pos-info-badge">
            Opened{' '}
            {(signal.timestamp.toDate ? signal.timestamp.toDate() : new Date(signal.timestamp))
              .toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
        {strategyName && (
          <span className="pos-info-badge">
            <span className="pos-badge-label">Strategy: </span>{strategyName}
          </span>
        )}
        <a className="view-btn" href={HL_APP_URL} target="_blank"
          rel="noopener noreferrer" style={{ marginLeft: 'auto' }}>
          View ↗
        </a>
      </div>

      {/* ── Row 3: Trailing Events (collapsible) ────────────────────────── */}
      <PositionTrailEvents
        coin={coin}
        direction={isLong ? 'LONG' : 'SHORT'}
        signalMs={signalMs}
      />

    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard() {
  const [signals,     setSignals]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [allFills,    setAllFills]    = useState([]);
  const [sideFilter,      setSideFilter]      = useState('ALL');
  const [coinFilter,      setCoinFilter]      = useState('ALL');
  const [strategyFilter,  setStrategyFilter]  = useState('ALL');
  const [dateFrom,        setDateFrom]        = useState('');
  const [dateTo,          setDateTo]          = useState('');

  const { positions, orders, mids, fundingRates, accountValue, withdrawable, dailyPnl } = useHyperliquid();

  useEffect(() => {
    const q = query(collection(db, 'signals'), orderBy('timestamp', 'desc'), limit(50));
    const unsub = onSnapshot(q, snap => {
      setSignals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => {
      console.error(err);
      setError('Failed to load signals.');
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    fetch(HL_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ type: 'userFills', user: HL_WALLET }),
    })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setAllFills(data); })
      .catch(() => {});
  }, []);

  const openSignalIds = useMemo(() => {
    const latestBySymbol = {};
    signals.forEach(s => {
      if (s.status !== 'executed') return;
      const ms = tsMs(s);
      if (!latestBySymbol[s.symbol] || ms > latestBySymbol[s.symbol].ms)
        latestBySymbol[s.symbol] = { id: s.id, ms };
    });
    const result = new Set();
    Object.values(latestBySymbol).forEach(({ id, ms }) => {
      const sig = signals.find(s => s.id === id);
      if (!sig) return;
      const coin     = (sig.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
      const dirLabel = (sig.direction || '').toUpperCase() === 'LONG' ? 'Long' : 'Short';
      const hasClosed = allFills.some(f =>
        f.coin === coin &&
        (f.dir || '').includes(dirLabel) &&
        (f.dir || '').includes('Close') &&
        f.time >= ms
      );
      if (!hasClosed) result.add(id);
    });
    return result;
  }, [signals, allFills]);

  const nextMsMap = useMemo(() => {
    const map = {};
    signals.forEach(sig => {
      const sigMs = tsMs(sig);
      const later = signals.filter(s => s.symbol === sig.symbol && s.id !== sig.id && tsMs(s) > sigMs && s.status !== 'duplicate_skipped');
      map[sig.id] = later.length > 0 ? tsMs(later[later.length - 1]) : null;
    });
    return map;
  }, [signals]);

  function computePnl(signal) {
    const result = tradeResult(signal, allFills, nextMsMap[signal.id]);
    return result ? result.net : null;
  }

  function toggleExpand(id) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // Latest 10 closed trades (exclude open positions and duplicates)
  const latestTen = useMemo(() => {
    return signals
      .filter(s => !openSignalIds.has(s.id) && s.status !== 'duplicate_skipped')
      .slice(0, 10)
      .map(s => ({ ...s, _pnl: computePnl(s) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals, allFills, nextMsMap, openSignalIds]);

  const longs  = positions.filter(p => parseFloat(p.position.szi) > 0);
  const shorts = positions.filter(p => parseFloat(p.position.szi) < 0);
  const totalPnl = positions.reduce((sum, p) => sum + parseFloat(p.position.unrealizedPnl || 0), 0);
  const allTimePnl = signals
    .filter(s => !openSignalIds.has(s.id) && s.status !== 'duplicate_skipped')
    .reduce((sum, s) => {
      const pnl = computePnl(s);
      return pnl != null ? sum + pnl : sum;
    }, 0);

  const positionCoins = ['ALL', ...positions.map(p => p.position.coin)];

  const positionStrategies = useMemo(() => {
    const set = new Set();
    positions.forEach(pos => {
      const isLong = parseFloat(pos.position.szi) > 0;
      const sig = getMatchingSignal(signals, pos.position.coin, isLong);
      const strat = sig?.strategy || sig?.source;
      if (strat) set.add(strat);
    });
    return ['ALL', ...Array.from(set).sort()];
  }, [positions, signals]);

  const filteredPositions = positions.filter(pos => {
    const isLong = parseFloat(pos.position.szi) > 0;
    if (sideFilter === 'LONG' && !isLong) return false;
    if (sideFilter === 'SHORT' && isLong) return false;
    if (coinFilter !== 'ALL' && pos.position.coin !== coinFilter) return false;
    if (strategyFilter !== 'ALL') {
      const sig = getMatchingSignal(signals, pos.position.coin, isLong);
      const strat = sig?.strategy || sig?.source || null;
      if (strat !== strategyFilter) return false;
    }
    if (dateFrom || dateTo) {
      const sig = getMatchingSignal(signals, pos.position.coin, isLong);
      const sigMs = sig ? tsMs(sig) : 0;
      if (dateFrom && sigMs < new Date(dateFrom).getTime()) return false;
      if (dateTo   && sigMs > new Date(dateTo + 'T23:59:59').getTime()) return false;
    }
    return true;
  });

  const hasActiveFilter = sideFilter !== 'ALL' || coinFilter !== 'ALL' || strategyFilter !== 'ALL' || dateFrom || dateTo;
  function clearPosFilters() {
    setSideFilter('ALL'); setCoinFilter('ALL'); setStrategyFilter('ALL'); setDateFrom(''); setDateTo('');
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <div className="page-header-main">
          <h1>Dashboard</h1>
          <div className="live-indicator">
            <span className="status-dot live" />
            <span className="live-label">Live</span>
          </div>
        </div>
        <p>Hyperliquid positions · refreshes every 5s</p>
      </div>

      <div className="stat-cards">
        <StatCard label="Longs Open" value={longs.length}
          sub={longs.map(p => p.position.coin).join(', ') || 'None'}
          valueColor={longs.length > 0 ? 'var(--green)' : undefined} />
        <StatCard label="Shorts Open" value={shorts.length}
          sub={shorts.map(p => p.position.coin).join(', ') || 'None'}
          valueColor={shorts.length > 0 ? 'var(--red)' : undefined} />
        <StatCard label="Daily PnL" value={formatPnl(dailyPnl)}
          pct={accountValue > 0 ? `(${dailyPnl >= 0 ? '+' : ''}${((dailyPnl / accountValue) * 100).toFixed(1)}%)` : null}
          sub="Realized today"
          valueColor={dailyPnl >= 0 ? 'var(--green)' : 'var(--red)'} />
        <StatCard label="Unrealized PnL" value={formatPnl(totalPnl)}
          pct={accountValue > 0 ? `(${totalPnl >= 0 ? '+' : ''}${((totalPnl / accountValue) * 100).toFixed(1)}%)` : null}
          sub="Live · all open positions"
          valueColor={totalPnl > 0 ? 'var(--green)' : totalPnl < 0 ? 'var(--red)' : undefined} />
        <StatCard label="Total PnL"
          value={formatPnl(allTimePnl)}
          sub="Closed trades, after fees"
          valueColor={allTimePnl > 0 ? 'var(--green)' : allTimePnl < 0 ? 'var(--red)' : undefined} />
        <StatCard label="Account Balance"
          value={`$${accountValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={withdrawable > 0 ? `Withdrawable: $${withdrawable.toFixed(2)} USDC` : 'USDC · Hyperliquid'} />
      </div>

      {/* ── Open Positions ────────────────────────────────────────────────── */}
      <div className="section">
        <div className="section-header">
          <span className="section-title">Open Positions</span>
          <span className="section-sub">Live from Hyperliquid {HL_NETWORK} · refreshes every 5s</span>
        </div>

        <div className="pos-filter-bar card" style={{ display: 'flex' }}>
            <div className="filter-group">
              <label className="filter-label">Side</label>
              <div className="side-filter-btns">
                {['ALL', 'LONG', 'SHORT'].map(side => (
                  <button key={side}
                    className={`side-btn side-btn--${side.toLowerCase()}${sideFilter === side ? ' active' : ''}`}
                    onClick={() => setSideFilter(side)}>
                    {side}
                  </button>
                ))}
              </div>
            </div>
            <div className="filter-group">
              <label className="filter-label">Symbol</label>
              <select className="filter-select" value={coinFilter} onChange={e => setCoinFilter(e.target.value)}>
                {positionCoins.map(c => (
                  <option key={c} value={c}>{c === 'ALL' ? 'All Symbols' : c}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Strategy</label>
              <select className="filter-select" value={strategyFilter} onChange={e => setStrategyFilter(e.target.value)}>
                {positionStrategies.map(s => (
                  <option key={s} value={s}>{s === 'ALL' ? 'All Strategies' : s}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">From</label>
              <input type="date" className="filter-select" value={dateFrom}
                max={dateTo || toDateInput(new Date())} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div className="filter-group">
              <label className="filter-label">To</label>
              <input type="date" className="filter-select" value={dateTo}
                min={dateFrom} max={toDateInput(new Date())} onChange={e => setDateTo(e.target.value)} />
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {hasActiveFilter && <button className="clear-btn" onClick={clearPosFilters}>Clear</button>}
              <span className="pos-filter-count">{filteredPositions.length} of {positions.length}</span>
            </div>
          </div>

        {positions.length > 0 ? (
          <div className="positions-list">
            {filteredPositions.length > 0 ? filteredPositions.map((pos, i) => (
              <PositionRow key={i} position={pos} orders={orders} mids={mids}
                fundingRates={fundingRates}
                signal={getMatchingSignal(signals, pos.position.coin, parseFloat(pos.position.szi) > 0)} />
            )) : (
              <div className="positions-empty">No positions match filters</div>
            )}
          </div>
        ) : (
          <div className="positions-empty">No open positions</div>
        )}
      </div>

      {/* ── Latest 10 Trades ─────────────────────────────────────────────── */}
      {!loading && !error && (
        <div className="section" style={{ marginTop: 12 }}>
          <div className="section-header">
            <span className="section-title">Latest 10 Trades</span>
            <Link to="/history" className="view-all-link">View All History →</Link>
          </div>

          {latestTen.length === 0 ? (
            <div className="positions-empty">No closed trades yet</div>
          ) : (
            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="data-table dash-latest-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Side</th>
                    <th>Symbol</th>
                    <th>Strategy</th>
                    <th>Entry</th>
                    <th>SL</th>
                    <th>TP</th>
                    <th>Size</th>
                    <th>PnL</th>
                    <th>Time</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {latestTen.map(signal => {
                    const coin         = (signal.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
                    const isLong       = (signal.direction || '').toUpperCase() !== 'SHORT';
                    const open         = expandedIds.has(signal.id);
                    const pnl          = signal._pnl;
                    const nextSignalMs = nextMsMap[signal.id] ?? null;
                    return [
                      <tr
                        key={signal.id}
                        className={open ? 'row--expanded' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleExpand(signal.id)}
                      >
                        <td className="hist-trade-id">{signal.tradeId || '—'}</td>
                        <td>
                          <span className={`side-badge side-badge--${isLong ? 'long' : 'short'}`}>
                            {isLong ? 'LONG' : 'SHORT'}
                          </span>
                          {' '}
                          <span className="leverage-badge">{signal.leverage || 5}x {signal.leverageType || 'Cross'}</span>
                        </td>
                        <td>
                          <div className="history-coin">
                            <CoinIcon base={coin} size={18} />
                            <span className="history-symbol">{coin}</span>
                          </div>
                        </td>
                        <td className="strategy-cell">{signal.strategy || signal.source || '—'}</td>
                        <td>{formatPrice(signal.fill_price || signal.price)}</td>
                        <td className="text-red">{formatPrice(signal.sl_price)}</td>
                        <td className="text-green">{formatPrice(signal.tp_price)}</td>
                        <td>{signal.size != null ? `$${signal.size}` : '—'}</td>
                        <td className={pnl != null ? (pnl >= 0 ? 'pnl-positive' : 'pnl-negative') : ''}>
                          {pnl != null ? `${pnl >= 0 ? '+' : '−'}$${Math.abs(pnl).toFixed(2)}` : '—'}
                        </td>
                        <td className="time-cell">{formatDateTime(signal.timestamp)}</td>
                        <td className="td-expand-col">{open ? '▲' : '▼'}</td>
                      </tr>,
                      open && (
                        <tr key={`${signal.id}-detail`} className="row--detail">
                          <td colSpan="11">
                            <TradeDetailPanel signal={signal} nextSignalMs={nextSignalMs} />
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="loading-state"><div className="spinner" /><p>Connecting to Firestore…</p></div>
      )}
      {error && <div className="card" style={{ color: 'var(--red)' }}>{error}</div>}
    </div>
  );
}

function StatCard({ label, value, sub, valueColor, pct }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={valueColor ? { color: valueColor } : undefined}>{value}</div>
      {pct && <div className="stat-pct" style={valueColor ? { color: valueColor } : undefined}>{pct}</div>}
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export default Dashboard;
